import { useEffect, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, PageShell } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Check, X } from 'lucide-react';

const TYPE_LABEL = { full_day: 'Full day', half_day: 'Half day', sick: 'Sick', casual: 'Casual', earned: 'Earned' };

export default function Leaves() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', leave_type: 'full_day', reason: '' });

  const load = async () => {
    try { const { data } = await api.get('/leaves'); setRows(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    if (!form.start_date || !form.end_date || !form.reason) { toast.error('Please fill all fields'); return; }
    try { await api.post('/leaves', form); toast.success('Leave submitted'); setOpen(false); load(); setForm({ start_date: '', end_date: '', leave_type: 'full_day', reason: '' }); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const act = async (id, status) => {
    try { await api.patch(`/leaves/${id}/action`, { status }); toast.success(`Leave ${status}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const badge = (s) => s === 'approved' ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' : s === 'rejected' ? 'bg-red-500/15 text-red-500 border-red-500/30' : 'bg-amber-500/15 text-amber-500 border-amber-500/30';

  const canApprove = ['admin', 'manager'].includes(user?.role);
  const pending = rows.filter(r => r.status === 'pending').length;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Time off"
        title="Leaves & approvals"
        subtitle={canApprove ? `${pending} pending request${pending === 1 ? '' : 's'} awaiting your review.` : 'Apply for half-day or full-day leave and track approvals here.'}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="apply-leave-btn" className="bg-primary hover:bg-primary/90 text-white"><Plus className="w-4 h-4 mr-2" />Apply Leave</Button>
            </DialogTrigger>
            <DialogContent data-testid="leave-dialog">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black">Apply for leave</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Start date</Label>
                    <Input type="date" data-testid="leave-start" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div>
                    <Label>End date</Label>
                    <Input type="date" data-testid="leave-end" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.leave_type} onValueChange={(v) => setForm(f => ({ ...f, leave_type: v }))}>
                    <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} placeholder="Explain briefly…" />
                </div>
                <Button data-testid="leave-submit" onClick={apply} className="w-full bg-primary hover:bg-primary/90 text-white">Submit request</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="tactical-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              {canApprove && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody data-testid="leaves-tbody">
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.user_name}</TableCell>
                <TableCell>{r.start_date} → {r.end_date}</TableCell>
                <TableCell>{TYPE_LABEL[r.leave_type] || r.leave_type}</TableCell>
                <TableCell className="max-w-xs truncate">{r.reason}</TableCell>
                <TableCell><Badge variant="outline" className={badge(r.status)}>{r.status}</Badge></TableCell>
                {canApprove && (
                  <TableCell className="text-right">
                    {r.status === 'pending' ? (
                      <div className="flex justify-end gap-2">
                        <Button data-testid={`approve-${r.id}`} size="sm" onClick={() => act(r.id, 'approved')} className="bg-emerald-500 hover:bg-emerald-600 text-white"><Check className="w-3 h-3 mr-1" />Approve</Button>
                        <Button data-testid={`reject-${r.id}`} size="sm" variant="outline" onClick={() => act(r.id, 'rejected')} className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"><X className="w-3 h-3 mr-1" />Reject</Button>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">{r.action_by_name}</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={canApprove ? 6 : 5} className="text-center text-muted-foreground py-10">No leaves yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </PageShell>
  );
}
